import { z } from "zod";

import { CONCEPT_MAX_LENGTH, SAVED_WALLET_ALIAS_MAX_LENGTH } from "../../constants/cryptoSwaps.constants";
import type { OtcSavedWalletOwnership } from "../../types/cryptoSwaps.types";
import { checkCryptoSwapAddress } from "../../utils/cryptoSwapAddress";

export interface DestinationMessages {
  addressRequired: string;
  addressInvalid: string;
  networkUnsupported: string;
  conceptTooLong: string;
  aliasTooLong: string;
  holderRequired: string;
}

export interface DestinationValues {
  address: string;
  concept: string;
  /** A saved wallet picked from the list; its address fills `address`. */
  savedWalletId: string | null;
  /** Save a typed address for future orders, behind the user's second factor. */
  save: boolean;
  alias: string;
  ownership: OtcSavedWalletOwnership;
  holderName: string;
}

export const EMPTY_DESTINATION: DestinationValues = {
  address: "",
  concept: "",
  savedWalletId: null,
  save: false,
  alias: "",
  ownership: "OWN",
  holderName: "",
};

/** Field by field: the form emits a new object on every change, and any field may be the one that moved. */
export function sameDestination(a: DestinationValues, b: DestinationValues): boolean {
  return (
    a.address === b.address &&
    a.concept === b.concept &&
    a.savedWalletId === b.savedWalletId &&
    a.save === b.save &&
    a.alias === b.alias &&
    a.ownership === b.ownership &&
    a.holderName === b.holderName
  );
}

/** An ORDER input: editing it never re-prices. The checksum lives here because it decides recoverability. */
export function buildDestinationSchema(network: string, messages: DestinationMessages) {
  return z
    .object({
      address: z.string(),
      concept: z.string(),
      savedWalletId: z.string().nullable(),
      save: z.boolean(),
      alias: z.string(),
      ownership: z.enum(["OWN", "THIRD_PARTY"]),
      holderName: z.string(),
    })
    .superRefine((values, ctx) => {
      const check = checkCryptoSwapAddress(network, values.address);
      if (check.status === "unsupported_network") {
        ctx.addIssue({ code: "custom", path: ["address"], message: messages.networkUnsupported });
      } else if (check.status === "empty") {
        ctx.addIssue({ code: "custom", path: ["address"], message: messages.addressRequired });
      } else if (check.status === "invalid") {
        ctx.addIssue({ code: "custom", path: ["address"], message: messages.addressInvalid });
      }
      if (values.concept.trim().length > CONCEPT_MAX_LENGTH) {
        ctx.addIssue({ code: "custom", path: ["concept"], message: messages.conceptTooLong });
      }
      if (values.save && !values.savedWalletId) {
        if (values.alias.trim().length > SAVED_WALLET_ALIAS_MAX_LENGTH) {
          ctx.addIssue({ code: "custom", path: ["alias"], message: messages.aliasTooLong });
        }
        if (values.ownership === "THIRD_PARTY" && !values.holderName.trim()) {
          ctx.addIssue({ code: "custom", path: ["holderName"], message: messages.holderRequired });
        }
      }
    });
}
